<ng-template #pageTitle>
  <h5 class="text-neutral-900 font-semibold text-2xl ps-2xl">
    {{ 'requests_screen_title' | transloco }}
  </h5>
</ng-template>

<ng-template #searchField>
  <lib-url-search
    #urlSearch
    [placeholder]="'create_request_screen_search_input_placeholder' | transloco"
    (searchValue)="currentKeyword.set($event)" />
</ng-template>

<ng-template #requestListBody>
  @if (isLoading()) {
    <lib-overlay-loader />
  } @else {
    @if (requestList().length) {
      <div class="w-full gap-lg cards-container">
        @for (request of requestList(); track request.id; let index = $index) {
          <lib-request-card [request]="request" />
        }
      </div>
    } @else {
      <div class="flex flex-col justify-center items-center pt-8xl">
        <lib-table-empty-state
          [noDataTitle]="'table_no_data_title' | transloco"
          [noDataDescription]="'section_no_data_description' | transloco" />
      </div>
    }
  }
</ng-template>

<ng-template #requestListPaginator>
  <lib-paginator
    [currentPage]="page()"
    [totalPages]="totalPages()"
    [totalItems]="totalItems()"
    [pageItemCount]="requestList().length"
    [nextBtnLabel]="'pagination_next_button_label' | transloco"
    [previousBtnLabel]="'pagination_previous_button_label' | transloco"
    (goTo)="handlePagination($event)"
    (next)="handlePagination($event)"
    (previous)="handlePagination($event)" />
</ng-template>

@if (floating()) {
  <!-- UI_Version_2: categories as filter chips inside the card; grid/list views, search and pagination from lib-filtered-table -->
  <ng-template #pageHeading>
    <div>
      <ng-container [ngTemplateOutlet]="pageTitle" />
      @if (totalWorkflows()) {
        <p class="text-sm text-neutral-500 mt-x-sm ps-2xl" data-page-summary>
          {{
            'requests_screen_summary' | transloco: { count: totalWorkflows() }
          }}
        </p>
      }
    </div>
  </ng-template>

  <div class="h-full flex flex-col" [class]="floatingPageClasses()">
    <div class="mb-lg">
      <ng-container [ngTemplateOutlet]="pageHeading" />
    </div>

    @if (tableReady()) {
      <lib-filtered-table
        data-request-table
        [tableId]="'request-list'"
        [chipFilters]="categoryFilters()"
        [searchPlaceholder]="'create_request_screen_search_input_placeholder'"
        [columns]="columns()"
        [dataSource]="requestList()"
        [meta]="meta()"
        [totalItems]="meta()?.total ?? 0"
        [isLoadingData]="isLoading()"
        [isTableExportable]="false"
        [allowViewToggle]="true"
        [showGridListTabs]="true"
        [enableRowClick]="false"
        [isGalleryView]="isGalleryView()"
        (isGalleryViewChange)="setGalleryView($event)"
        [(payload)]="payload"
        [galleryCardTemplateRef]="requestCard"
        [galleryClass]="'w-full gap-lg grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]'"
        [lastColumnAlignment]="'justify-end'"
        [applyOnStreamChanges]="loadRows$"
        (streamSucceeds)="applyRows($event)"
        (handleApiCall)="loadRows()"
        [tableColumnTemplates]="[
          { identifier: 'number', template: numberCell },
          { identifier: 'workflow', template: workflowCell },
          { identifier: 'category', template: categoryCell },
          { identifier: 'actions', template: actionsCell },
        ]">
        <ng-template #numberCell let-i="i">
          <!-- Numbered across pages, like the other tables -->
          <span data-request-number class="font-semibold">
            {{
              ((meta()?.current_page || 1) - 1) * (meta()?.per_page || 25) +
                (i + 1)
            }}
          </span>
        </ng-template>

        <ng-template #workflowCell let-row>
          <!-- Wide enough that most names stay on one line, keeping rows short -->
          <div
            data-request-workflow-cell
            class="flex min-w-[29rem] max-w-[40rem] flex-col whitespace-normal">
            <span class="text-sm font-semibold text-neutral-900">
              {{ row.name }}
            </span>
            @if (row.description) {
              <span class="line-clamp-1 text-xs text-neutral-500">
                {{ row.description }}
              </span>
            }
          </div>
        </ng-template>

        <ng-template #categoryCell let-row>
          <span
            data-request-category-cell
            class="flex items-center gap-sm text-sm text-neutral-700">
            <span
              class="size-2 shrink-0 rounded-full"
              [style.background-color]="
                row.category?.color || fallbackCategoryColor
              "></span>
            {{ row.category?.name }}
          </span>
        </ng-template>

        <ng-template #actionsCell let-row>
          <lib-request-actions class="justify-end" [request]="row" />
        </ng-template>

        <ng-template #requestCard let-card>
          <lib-request-card [request]="card" />
        </ng-template>
      </lib-filtered-table>
    }
  </div>
} @else {
  <div class="h-full flex flex-col justify-between">
    <div class="flex flex-col gap-2xl">
      <div
        class="my-2xl py-lg flex items-center justify-between border-b border-neutral-200">
        <ng-container [ngTemplateOutlet]="pageTitle" />
        <ng-container [ngTemplateOutlet]="searchField" />
      </div>

      @if (!categoriesLoading()) {
        <lib-tabs
          [tabSegmented]="systemTabs()"
          [tabPill]="false"
          [tabsBorder]="true"
          class="pb-2xl"
          [class.border-b]="false"
          [class.border-neutral-200]="false"
          [enableTabRouting]="true"
          [paramKey]="REQUEST_LIST_TAB_PARAM_KEY"
          (selectTab)="handleTabClick($event)">
          @for (item of requestCategoryList(); track $index) {
            <lib-tab
              [tabTitle]="item.name"
              [showTabTag]="true"
              [tabRoutingParam]="item.id.toString()"
              [tabTag]="
                item.id === selectedCategory().id
                  ? selectedCategory().total
                  : item.total
              " />
          }
        </lib-tabs>
      }
      <ng-container [ngTemplateOutlet]="requestListBody" />
    </div>

    <div class="mt-2xl">
      <ng-container [ngTemplateOutlet]="requestListPaginator" />
    </div>
  </div>
}
